import React, { useState } from 'react';

const sectors = [
  'Manufacturing',
  'IT/ITeS',
  'Pharmaceuticals',
  'Automobile',
  'Textiles',
  'Food Processing',
  'Other'
];

const timeSlots = [
  '09:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
  '02:00 PM', '03:00 PM', '04:00 PM', '05:00 PM'
];

const VirtualMeetingForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    companyName: '',
    sector: '',
    meetingDate: '',
    meetingTime: '',
    topics: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.meetingDate) newErrors.meetingDate = 'Meeting date is required';
    if (!formData.meetingTime) newErrors.meetingTime = 'Meeting time is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsSubmitting(true);
      //console.log('Virtual Meeting Form submitted:', formData);
      // Simulate API call
      setTimeout(() => {
        setIsSubmitting(false);
        // Show success message or redirect
      }, 1500);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Schedule Virtual Meeting</h2>
      <p className="text-gray-600 mb-6">
        Connect with our investment officers via video conference
      </p>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm ${
                errors.fullName ? 'border-red-500' : ''
              }`}
              placeholder="John Doe"
            />
            {errors.fullName && (
              <p className="mt-1 text-sm text-red-600">{errors.fullName}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm ${
                errors.email ? 'border-red-500' : ''
              }`}
              placeholder="john@company.com"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
              Phone Number
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
              placeholder="+1 234 567 8900"
            />
          </div>
          
          <div className="space-y-2">
            <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">
              Company Name
            </label>
            <input
              type="text"
              id="companyName"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
              placeholder="Your Company"
            />
          </div>
          
          <div className="space-y-2">
            <label htmlFor="sector" className="block text-sm font-medium text-gray-700">
              Sector of Interest
            </label>
            <select
              id="sector"
              name="sector"
              value={formData.sector}
              onChange={handleChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
            >
              <option value="">Select sector</option>
              {sectors.map((sector) => (
                <option key={sector} value={sector}>
                  {sector}
                </option>
              ))}
            </select>
          </div>
          
          <div className="space-y-2">
            <label htmlFor="meetingDate" className="block text-sm font-medium text-gray-700">
              Preferred Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              id="meetingDate"
              name="meetingDate"
              value={formData.meetingDate}
              onChange={handleChange}
              min={new Date().toISOString().split('T')[0]}
              className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm ${
                errors.meetingDate ? 'border-red-500' : ''
              }`}
            />
            {errors.meetingDate && (
              <p className="mt-1 text-sm text-red-600">{errors.meetingDate}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <label htmlFor="meetingTime" className="block text-sm font-medium text-gray-700">
              Preferred Time <span className="text-red-500">*</span>
            </label>
            <select
              id="meetingTime"
              name="meetingTime"
              value={formData.meetingTime}
              onChange={handleChange}
              className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm ${
                errors.meetingTime ? 'border-red-500' : ''
              }`}
            >
              <option value="">Select time</option>
              {timeSlots.map((time) => (
                <option key={time} value={time}>
                  {time}
                </option>
              ))}
            </select>
            {errors.meetingTime && (
              <p className="mt-1 text-sm text-red-600">{errors.meetingTime}</p>
            )}
          </div>
        </div>
        
        <div className="space-y-2">
          <label htmlFor="topics" className="block text-sm font-medium text-gray-700">
            Topics to Discuss
          </label>
          <textarea
            id="topics"
            name="topics"
            rows={4}
            value={formData.topics}
            onChange={handleChange}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
            placeholder="What would you like to discuss? (e.g., available plots, incentives, infrastructure)"
          />
        </div>
        
        <div className="bg-gray-50 p-4 rounded-md">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Meeting Details</h3>
          <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
            <li>Duration: 30-45 minutes</li>
            <li>Platform: Bharat Online Sabha</li>
          </ul>
          <p className="mt-3 text-sm text-gray-600 italic">
            You'll receive a calendar invite via email
          </p>
        </div>
        
        <div className="pt-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full md:w-auto px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors duration-200 ${
              isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            {isSubmitting ? 'Scheduling...' : 'Schedule Meeting'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default VirtualMeetingForm;
